// Локальный журнал диагностики: только метаданные запросов (метод, путь без id и query, статус,
// request id, длительность). Никаких токенов, тел запросов/ответов и текстов сообщений.
// Хранится в localStorage (переживает перезагрузку вкладки), ограничен по размеру.
const KEY = 'hc_diag';
const MAX = 150;

export type DiagKind = 'api' | 'refresh' | 'conn' | 'app';

export interface DiagEntry {
  t: number; // ms epoch
  kind: DiagKind;
  what: string; // «GET /api/v1/chats/{id}», «refresh», «ws:connected» …
  status?: number; // HTTP-статус; 0 — сетевая ошибка
  rid?: string;
  ms?: number;
  note?: string; // короткая причина (имя ошибки), без данных пользователя
}

let buf: DiagEntry[] = load();
const subs = new Set<() => void>();

function load(): DiagEntry[] {
  try {
    const raw = localStorage.getItem(KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.slice(-MAX) : [];
  } catch {
    return [];
  }
}

function save() {
  try {
    localStorage.setItem(KEY, JSON.stringify(buf));
  } catch {
    /* хранилище недоступно — журнал остаётся в памяти */
  }
}

/** Путь без query, UUID и числовых id → «/api/v1/chats/{id}/messages». */
export function normalizePath(path: string): string {
  const p = path.split('?')[0].split('#')[0];
  return p
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '{id}')
    .replace(/\/\d+(?=\/|$)/g, '/{id}');
}

export function diag(e: Omit<DiagEntry, 't'>) {
  buf.push({ t: Date.now(), ...e });
  if (buf.length > MAX) buf = buf.slice(-MAX);
  save();
  subs.forEach((f) => f());
}

export function diagEntries(): DiagEntry[] {
  return buf;
}

export function diagClear() {
  buf = [];
  save();
  subs.forEach((f) => f());
}

export function diagSubscribe(f: () => void): () => void {
  subs.add(f);
  return () => subs.delete(f);
}

/** Запрос стоит в журнале, если он упал или был медленным (иначе журнал забьётся успехами). */
export function shouldLogApi(status: number, ms: number): boolean {
  return status === 0 || status >= 400 || ms > 3000;
}

export function newRequestId(): string {
  const a = new Uint8Array(8);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(a);
  else for (let i = 0; i < a.length; i++) a[i] = (Math.random() * 256) | 0;
  return 'w-' + Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('');
}

const pad = (n: number) => String(n).padStart(2, '0');
function fmtTime(t: number): string {
  const d = new Date(t);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** Текстовый отчёт для просмотра и копирования пользователем. Серверные адреса не включаются. */
export function buildReport(extra: Record<string, string>): string {
  const head = Object.entries(extra).map(([k, v]) => `${k}: ${v}`);
  const lines = buf.map((e) => {
    const parts = [fmtTime(e.t), e.kind, e.what];
    if (e.status !== undefined) parts.push(String(e.status));
    if (e.ms !== undefined) parts.push(`${e.ms}ms`);
    if (e.rid) parts.push(`rid=${e.rid}`);
    if (e.note) parts.push(e.note);
    return parts.join(' ');
  });
  return [...head, '', ...lines].join('\n');
}
